import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAiDailyQuota, type AiQuotaSnapshot } from '../lib/aiDailyQuota';
import { localDateKey } from '../lib/dateKey';

export type MealLineItem = {
  label: string;
  calories: number;
  proteinG: number;
  carbsG: number;
  fatsG: number;
  fiberG?: number | null;
  sugarG?: number | null;
  sodiumMg?: number | null;
  source?: string | null;
  sourceKind?: 'official' | 'usda' | 'aggregate' | 'fatsecret' | null;
  measureState?: string | null;
  found?: boolean;
  error?: string | null;
};

export type MealEstimate = {
  suggestedName: string;
  calories: number;
  proteinG: number;
  carbsG: number;
  fatsG: number;
  fiberG: number | null;
  sugarG: number | null;
  sodiumMg: number | null;
  notes: string;
  confidence: string;
  items: MealLineItem[];
  meta: { model: string; usdaHits?: number };
  quota?: AiQuotaSnapshot;
};

export function totalsFromMealItems(items: MealLineItem[]): Pick<
  MealEstimate,
  'calories' | 'proteinG' | 'carbsG' | 'fatsG' | 'fiberG' | 'sugarG' | 'sodiumMg'
> {
  let calories = 0;
  let proteinG = 0;
  let carbsG = 0;
  let fatsG = 0;
  let fiberG = 0;
  let sugarG = 0;
  let sodiumMg = 0;
  let hasFiber = false;
  let hasSugar = false;
  let hasSodium = false;
  for (const it of items) {
    if (it.found === false) continue;
    calories += it.calories;
    proteinG += it.proteinG;
    carbsG += it.carbsG;
    fatsG += it.fatsG;
    if (it.fiberG != null) {
      fiberG += it.fiberG;
      hasFiber = true;
    }
    if (it.sugarG != null) {
      sugarG += it.sugarG;
      hasSugar = true;
    }
    if (it.sodiumMg != null) {
      sodiumMg += it.sodiumMg;
      hasSodium = true;
    }
  }
  const round1 = (n: number) => Math.round(n * 10) / 10;
  return {
    calories: Math.max(0, Math.round(calories)),
    proteinG: round1(Math.max(0, proteinG)),
    carbsG: round1(Math.max(0, carbsG)),
    fatsG: round1(Math.max(0, fatsG)),
    fiberG: hasFiber ? round1(Math.max(0, fiberG)) : null,
    sugarG: hasSugar ? round1(Math.max(0, sugarG)) : null,
    sodiumMg: hasSodium ? Math.max(0, Math.round(sodiumMg)) : null,
  };
}

type Props = {
  mealSlotLabel?: string;
  onApplyToQuickAdd: (est: MealEstimate) => void;
  onLogEstimate?: (est: MealEstimate) => void | Promise<void>;
};

function errMsg(e: unknown): string {
  if (e instanceof ApiError && e.body) {
    try {
      const j = JSON.parse(e.body) as { error?: string };
      if (j.error) return j.error;
    } catch {
      /* ignore */
    }
  }
  return e instanceof Error ? e.message : 'Could not analyze.';
}

export function NutritionAiPanel({ mealSlotLabel, onApplyToQuickAdd, onLogEstimate }: Props) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<MealEstimate | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { quota, refresh, setQuota } = useAiDailyQuota(true);
  const remaining = quota?.unlimited ? null : quota?.mealSearches.remaining ?? null;
  const mealCap = remaining === 0;

  async function analyze() {
    const description = text.trim();
    if (!description || mealCap) return;
    setBusy(true);
    setErr(null);
    setLast(null);
    try {
      const data = await api<MealEstimate>('/api/ai/meal-from-description', {
        method: 'POST',
        body: JSON.stringify({ description, dateKey: localDateKey() }),
      });
      const items = Array.isArray(data.items) ? data.items : [];
      const summed = items.length ? totalsFromMealItems(items) : data;
      setLast({
        ...data,
        items,
        calories: summed.calories,
        proteinG: summed.proteinG,
        carbsG: summed.carbsG,
        fatsG: summed.fatsG,
        fiberG: summed.fiberG,
        sugarG: summed.sugarG,
        sodiumMg: summed.sodiumMg,
      });
      if (data.quota) setQuota(data.quota);
      else void refresh();
    } catch (e) {
      setErr(errMsg(e));
      void refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-violet-500/20 bg-violet-500/5 p-4">
      <div className="text-sm font-semibold text-[var(--text-primary)]">Look up meal</div>
      <p className="mt-1 text-xs text-[var(--text-muted)]">
        Describe what you ate — we search the food database (Chick-fil-A, packaged foods, ingredients) and estimate anything it misses. Estimates are labeled and not 100% confidence. Say dry vs cooked.
        {quota?.unlimited
          ? ' Unlimited lookups on this account.'
          : quota
            ? ` ${quota.mealSearches.remaining} of ${quota.mealSearches.limit} lookups left today.`
            : ''}
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Chicken burrito bowl with rice, beans, cheese, guac…"
        rows={3}
        disabled={busy || mealCap}
        className="mt-3 w-full resize-y rounded-xl border border-white/10 bg-zinc-900/80 px-3 py-2 text-sm text-white outline-none focus:border-sky-500/40 disabled:opacity-50"
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy || mealCap || !text.trim()}
          onClick={() => void analyze()}
          className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? 'Looking up…' : mealCap ? 'Daily lookup limit reached' : 'Look up macros'}
        </button>
      </div>
      {err ? <p className="mt-3 text-sm text-red-300">{err}</p> : null}
      {last ? (
        <div className="mt-4 space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
          <div className="font-medium text-zinc-200">{last.suggestedName}</div>
          <div className="grid gap-1 text-xs text-zinc-400 sm:grid-cols-2">
            <span>
              <span className="text-zinc-500">Calories:</span> {last.calories} kcal
            </span>
            <span>
              <span className="text-zinc-500">Protein:</span> {last.proteinG} g
            </span>
            <span>
              <span className="text-zinc-500">Carbs:</span> {last.carbsG} g
            </span>
            <span>
              <span className="text-zinc-500">Fat:</span> {last.fatsG} g
            </span>
            {last.fiberG != null ? (
              <span>
                <span className="text-zinc-500">Fiber:</span> {last.fiberG} g
              </span>
            ) : null}
            {last.sugarG != null ? (
              <span>
                <span className="text-zinc-500">Sugar:</span> {last.sugarG} g
              </span>
            ) : null}
            {last.sodiumMg != null ? (
              <span>
                <span className="text-zinc-500">Sodium:</span> {last.sodiumMg} mg
              </span>
            ) : null}
          </div>
          {last.items.length > 0 ? (
            <div className="rounded-lg border border-white/5 bg-black/25 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                Each food (we add these)
              </div>
              <ul className="mt-2.5 list-none space-y-3 text-xs">
                {last.items.map((it, idx) => (
                  <li key={`${idx}-${it.label}`} className="text-zinc-300">
                    <div className="font-medium leading-snug text-zinc-100">{it.label}</div>
                    {it.measureState && it.measureState !== 'unknown' ? (
                      <div className="mt-0.5 text-[10px] uppercase tracking-wide text-zinc-500">
                        {it.measureState === 'dry'
                          ? 'Dry / uncooked measure'
                          : it.measureState === 'raw'
                            ? 'Raw / uncooked weight'
                            : 'Cooked / prepared weight'}
                      </div>
                    ) : null}
                    {it.found === false ? (
                      <p className="mt-1.5 text-amber-200/90">{it.error || 'Could not look this up — enter it manually.'}</p>
                    ) : (
                      <>
                        {it.source ? (
                          <div className="mt-0.5 text-[10px] text-zinc-500">
                            {it.source}
                            {it.sourceKind === 'aggregate'
                              ? ' · AI estimate'
                              : it.sourceKind === 'usda'
                                ? ' · USDA'
                                : it.sourceKind === 'fatsecret'
                                  ? ' · food database'
                                  : ' · official'}
                          </div>
                        ) : null}
                        {it.sourceKind === 'aggregate' ? (
                          <p className="mt-1 text-[11px] text-amber-200/85">
                            AI estimate — not USDA published facts, not 100% confidence. Adjust if you know better.
                          </p>
                        ) : null}
                        <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-zinc-500 marker:text-zinc-600">
                          <li className="pl-0.5">{it.calories} kcal</li>
                          <li className="pl-0.5">{it.proteinG} g protein</li>
                          <li className="pl-0.5">{it.carbsG} g carbs</li>
                          <li className="pl-0.5">{it.fatsG} g fat</li>
                        </ul>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {last.items.some((it) => it.sourceKind === 'aggregate') ? (
            <p className="text-xs text-amber-200/90">
              Totals include AI estimates. Those foods are approximate — not 100% confidence.
            </p>
          ) : null}
          {last.items.some((it) => it.found === false) ? (
            <p className="text-xs text-amber-200/90">
              Some items still have no numbers. Totals skip those — add them by hand if you want them in.
            </p>
          ) : null}
          {last.notes ? <p className="text-xs text-amber-200/80">{last.notes}</p> : null}
          <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
            {last.confidence} · {last.meta.model}
            {last.meta.usdaHits ? ` · USDA ${last.meta.usdaHits}` : ''}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!last.items.some((it) => it.found !== false && it.calories > 0)}
              onClick={() => onApplyToQuickAdd(last)}
              className="rounded-xl border border-sky-500/35 bg-sky-500/15 px-4 py-2 text-xs font-semibold text-sky-200 hover:bg-sky-500/25 disabled:opacity-40"
            >
              Use these macros
            </button>
            {onLogEstimate ? (
              <button
                type="button"
                disabled={!last.items.some((it) => it.found !== false && it.calories > 0)}
                onClick={() => void onLogEstimate(last)}
                className="rounded-xl bg-accent px-4 py-2 text-xs font-semibold text-white disabled:opacity-40"
              >
                Log to {mealSlotLabel ?? 'this meal'}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
